/**
 * Plausible script loader.
 *
 * The Plausible install snippet has two halves:
 *   1. A `<script>` tag pointing at the tracker (we proxy through
 *      `/js/script.js` → see functions/js/script.js.ts to bypass ad
 *      blockers and keep events on first-party domain).
 *   2. A tiny "queue shim" that defines `window.plausible(...)` *before*
 *      the script loads, so events fired during page paint don't get lost.
 *      The shim pushes arguments into `window.plausible.q`; once the real
 *      script loads it drains that queue and replaces the function.
 *
 * Loader behavior:
 *   - Idempotent. Two calls = one script tag.
 *   - Hostname guard: refuses to load when the runtime hostname doesn't
 *     match the configured analytics domain. Keeps localhost / preview
 *     (`*.pages.dev`) / staging environments out of production stats.
 *   - SSR-safe. No-ops on the server (no window, no document).
 *
 * Why a guard instead of separate dev/prod builds: copy + paste a preview
 * URL into a Twitter card debugger and the same JS bundle ships there.
 * We don't want every social scrape generating a "visit" event.
 *
 * Pageview-props mechanism (the `pageview-props` extension in our bundle):
 *   The tracker reads any attribute on its own `<script>` element whose
 *   name starts with `event-` and merges them into the `props` field of
 *   every event payload — including the auto-fired pageview. The script
 *   uses fallback semantics (`props[k] = props[k] || attrValue`), so
 *   props passed via `plausible(name, { props })` always win, and the
 *   stale script-tag attrs from initial mount don't override post-swap
 *   props on subsequent custom events.
 *
 *   Verified two ways:
 *     1. Canonical Plausible template, `tracker/src/plausible.js` v2.1.0:
 *        https://github.com/plausible/analytics/blob/v2.1.0/tracker/src/plausible.js
 *        (search the `pageview_props` block — `getAttributeNames().filter(...event-)`).
 *     2. The minified bundle we actually serve from
 *        `plausible.warborn.dev/js/script.hash.outbound-links.pageview-props.tagged-events.js`
 *        contains the same code path verbatim.
 *
 *   Attribute key shape is literally `event-<key>` (no `data-` prefix);
 *   we coerce values to strings since HTML attributes are always strings.
 */

/** Tiny stub plus argument queue, in the shape Plausible's tracker expects. */
type PlausibleStub = ((event: string, opts?: { props?: Record<string, unknown> }) => void) & {
  q?: unknown[][];
};

declare global {
  interface Window {
    plausible?: PlausibleStub;
  }
}

const SCRIPT_ID = 'nyx-plausible';
/** Same-origin paths — see the CF Pages function proxies. */
const SCRIPT_SRC = '/js/script.js';
const EVENT_API = '/api/event';

/** Returns true if the runtime should load + emit analytics. */
function shouldLoad(domain: string): boolean {
  if (typeof window === 'undefined') return false;
  if (!domain) return false;
  return window.location.hostname === domain;
}

/** Install the queue shim. Idempotent. */
function installShim(): void {
  if (window.plausible) return;
  const stub: PlausibleStub = function (...args: unknown[]) {
    (stub.q = stub.q ?? []).push(args);
  } as unknown as PlausibleStub;
  window.plausible = stub;
}

/** Primitive prop value as accepted by Plausible. HTML serializes to strings. */
export type PageviewPropValue = string | number | boolean;

/**
 * Load Plausible exactly once. Returns true if the script tag was injected
 * by this call (useful in tests; the provider ignores it).
 *
 * `pageviewProps` (optional) — props attached to the auto-fired initial
 * pageview via `event-<key>` attributes on the injected `<script>`. They
 * apply as a fallback to every event the tracker fires: any prop already
 * passed in `plausible(name, { props })` wins, so subsequent custom
 * events from `useTrack` (which already inject `{ album, phase }`) are
 * not affected by the initial-mount values frozen here.
 *
 * Idempotent: a second call with different props is a no-op (the script
 * tag already exists). Props must be applied *before* `appendChild` so
 * the auto-pageview, fired the moment the tracker boots, picks them up.
 */
export function loadPlausible(
  domain: string,
  pageviewProps?: Record<string, PageviewPropValue>
): boolean {
  if (!shouldLoad(domain)) return false;
  if (document.getElementById(SCRIPT_ID)) return false;

  installShim();

  const tag = document.createElement('script');
  tag.id = SCRIPT_ID;
  tag.defer = true;
  tag.src = SCRIPT_SRC;
  tag.dataset['domain'] = domain;
  tag.dataset['api'] = EVENT_API;
  if (pageviewProps) {
    for (const [key, value] of Object.entries(pageviewProps)) {
      tag.setAttribute(`event-${key}`, String(value));
    }
  }
  document.head.appendChild(tag);
  return true;
}

/**
 * Fire-and-forget event. The Plausible shim queues until the script loads;
 * if the visitor blocks the script outright the call is a silent no-op.
 *
 * Centralized here so the AnalyticsProvider hook never touches
 * `window.plausible` directly — keeps the surface area testable and the
 * compile-time types meaningful.
 */
export function sendEvent(name: string, props: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  const fn = window.plausible;
  if (!fn) return;
  fn(name, { props });
}
